/**
 * Robustly copy a string to the clipboard.
 *
 * Tries (in order):
 *   1. `navigator.clipboard.writeText` (modern path)
 *   2. Hidden `<textarea>` + `document.execCommand('copy')` (legacy fallback)
 *
 * Returns true on success, false on failure. Never throws.
 *
 * Why both? Some embedded WebViews (and our preview iframe) advertise
 * `navigator.clipboard` but throw a SecurityError / NotAllowedError when
 * called because the Clipboard-Write permission is gated by the parent
 * frame's permissions policy. The textarea + execCommand path is the
 * widely-supported escape hatch — works on iOS Safari WebViews, old
 * Chromium, sandboxed iframes, etc.
 */
export async function copyToClipboard(text) {
  if (!text) return false;

  // Path 1: modern Clipboard API.
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }

  // Path 2: hidden textarea + execCommand('copy').
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // Avoid scrolling-to-bottom on iOS by positioning it off-screen.
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '1px';
    textArea.style.height = '1px';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textArea);
    return Boolean(ok);
  } catch {
    return false;
  }
}
